import { Link2, Mail, Plus, UserMinus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { AccountRole, AddMemberOutcome, PendingInvitation } from "../../../shared/api-types";
import { AddMemberModal } from "../../components/AddMemberModal";
import { Button, Modal, Select } from "../../components/ui";
import { apiError, useLocale, useT } from "../../i18n";
import { apiJson } from "../../lib/api";
import { authClient } from "../../lib/auth";
import { useMe } from "../../lib/me";

type Member = {
  id: string;
  userId: string;
  role: string;
  user: { name: string; email: string };
};

const iconBtn =
  "rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 " +
  "dark:hover:bg-zinc-800 dark:hover:text-zinc-200";
const iconBtnDanger =
  "rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 " +
  "dark:hover:bg-red-950/40 dark:hover:text-red-400 disabled:pointer-events-none disabled:opacity-40";

/** Benutzer des aktiven Accounts (Account-Admins): einladen, Rolle, entfernen, offene Einladungen. */
export function MembersSettings({ myUserId }: { myUserId: string }) {
  const t = useT();
  const [locale] = useLocale();
  const { reload: reloadMe } = useMe();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvitation[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

  const load = useCallback(async () => {
    const [res, pending] = await Promise.all([
      authClient.organization.listMembers({ query: { limit: 100 } }),
      apiJson<PendingInvitation[]>("/api/account/invitations").catch(() => []),
    ]);
    if (res.data) setMembers(res.data.members as Member[]);
    else setMessage(res.error?.message ?? t.members.listError);
    setInvites(pending);
  }, [t]);

  async function inviteAction(path: string, method: "POST" | "DELETE", ok: string) {
    setMessage(null);
    setNotice(null);
    try {
      await apiJson(path, { method });
      setNotice(ok);
    } catch (err) {
      setMessage(apiError(t, err instanceof Error ? err.message : "unknown"));
    }
    await load();
  }

  function onAdded(outcome: AddMemberOutcome) {
    setAddOpen(false);
    setMessage(null);
    setNotice(outcome === "invited" ? t.members.invited : t.members.added);
    void load();
  }

  const dateFmt = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", {
    dateStyle: "medium",
  });

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<{ error: { message?: string } | null }>) {
    setMessage(null);
    const { error } = await action();
    if (error) setMessage(error.message ?? t.members.actionError);
    await load();
    // Eigene Rolle könnte sich geändert haben (Admin → Mitglied): Nav aktualisieren
    await reloadMe();
  }

  const owners = members.filter((m) => m.role === "owner").length;
  /** Letzter Admin — Rolle/Entfernen gesperrt (Server prüft ebenfalls). */
  const isLastOwner = (m: Member) => m.role === "owner" && owners <= 1;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t.members.title}</h3>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="size-4" />
          {t.members.add}
        </Button>
      </div>

      {message && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{message}</p>}
      {notice && <p className="mb-4 text-sm text-emerald-600 dark:text-emerald-400">{notice}</p>}

      <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
              <th className="px-4 py-3 font-medium">{t.members.name}</th>
              <th className="px-4 py-3 font-medium">{t.members.email}</th>
              <th className="px-4 py-3 font-medium">{t.members.role}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const self = m.userId === myUserId;
              const locked = isLastOwner(m);
              return (
                <tr
                  key={m.id}
                  className="border-b border-zinc-100 transition-colors last:border-0 hover:bg-zinc-50 dark:border-zinc-800/60 dark:hover:bg-zinc-800/40"
                >
                  <td className="px-4 py-3 font-medium">
                    {m.user.name}
                    {self && <span className="font-normal text-zinc-400"> {t.members.you}</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">{m.user.email}</td>
                  <td className="px-4 py-3">
                    {locked ? (
                      <span title={t.members.lastOwnerHint}>{t.members.roleOwner}</span>
                    ) : (
                      <Select
                        className="h-8"
                        value={m.role}
                        onChange={(e) =>
                          void run(() =>
                            authClient.organization.updateMemberRole({
                              memberId: m.id,
                              role: e.target.value as AccountRole,
                            }),
                          )
                        }
                      >
                        <option value="member">{t.members.roleMember}</option>
                        <option value="owner">{t.members.roleOwner}</option>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        title={locked ? t.members.lastOwnerHint : t.members.tooltipRemove}
                        disabled={locked}
                        onClick={() => setRemoveTarget(m)}
                        className={iconBtnDanger}
                      >
                        <UserMinus className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <>
          <h3 className="mb-3 mt-8 text-sm font-semibold">{t.members.pendingTitle}</h3>
          <p className="mb-3 text-sm text-zinc-500 dark:text-zinc-400">{t.members.pendingHint}</p>
          <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
                  <th className="px-4 py-3 font-medium">{t.members.email}</th>
                  <th className="px-4 py-3 font-medium">{t.members.role}</th>
                  <th className="px-4 py-3 font-medium">{t.members.validUntil}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-zinc-100 last:border-0 dark:border-zinc-800/60"
                  >
                    <td className="px-4 py-3">{inv.email}</td>
                    <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">
                      {inv.role === "owner" ? t.members.roleOwner : t.members.roleMember}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">
                      {dateFmt.format(new Date(inv.expiresAt))}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          title={t.members.copyLink}
                          onClick={() =>
                            void navigator.clipboard.writeText(inv.url).then(
                              () => setNotice(t.members.linkCopied),
                              () => setMessage(t.common.error),
                            )
                          }
                          className={iconBtn}
                        >
                          <Link2 className="size-4" />
                        </button>
                        <button
                          title={t.members.resend}
                          onClick={() =>
                            void inviteAction(
                              `/api/account/invitations/${inv.id}/resend`,
                              "POST",
                              t.members.resent,
                            )
                          }
                          className={iconBtn}
                        >
                          <Mail className="size-4" />
                        </button>
                        <button
                          title={t.members.cancelInvite}
                          onClick={() =>
                            void inviteAction(
                              `/api/account/invitations/${inv.id}`,
                              "DELETE",
                              t.members.canceled,
                            )
                          }
                          className={iconBtnDanger}
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AddMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={onAdded}
        url="/api/account/members"
        title={t.members.addTitle}
      />

      <Modal
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={t.members.removeTitle(removeTarget?.user.name ?? "")}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          {removeTarget?.userId === myUserId ? t.members.removeSelfText : t.members.removeText}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setRemoveTarget(null)}>
            {t.common.cancel}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              const target = removeTarget;
              setRemoveTarget(null);
              if (!target) return;
              const self = target.userId === myUserId;
              void run(() =>
                authClient.organization.removeMember({ memberIdOrEmail: target.id }),
              ).then(() => {
                // Sich selbst entfernt → Sitzung hängt an keinem Account mehr: neu laden
                if (self) window.location.assign("/");
              });
            }}
          >
            {t.members.remove}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
